import  { useEffect, useMemo, useState } from 'react';
import TableContainer from 'Common/TableContainer';
import { Dropdown } from 'Common/Components/Dropdown';
import { MoreHorizontal, FileEdit, Trash2, Plus } from 'lucide-react';
import DeleteModal from 'Common/DeleteModal';
import Modal from 'Common/Components/Modal';
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";
import {
    getWalletList,
    updateWallet,
    addWallet,
    deleteWallet
} from "../../helpers/fakebackend_helper";
import { ToastContainer } from 'react-toastify';

interface WalletDTO {
  walletId: string;
  name: string;
  description: string;
  balance: number;
  amount: number;
}

const WalletList = () => {
  const [data, setData] = useState<WalletDTO[]>([]);
  const [eventData, setEventData] = useState<WalletDTO | null>(null);
  const [show, setShow] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const fetchWallets = async () => {
    showLoader();

    try {
      const response = await getWalletList();
      const wallets = response.data?._embedded?.wallets || [];
      setData(wallets);
    } catch (error) {
      toast.error("Error fetching wallets: " + error, { autoClose: 2000 });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchWallets();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
        name: "",
        description: "",
        balance: 0,
        amount: 0,
    },
    validationSchema: Yup.object({
        name: Yup.string().required("Nombre son requeridos"),
        description: Yup.string().required("Descripción son requeridos"),
        amount: Yup.number().required("Monto es requerido"),
    }),

    onSubmit: async (values) => {
      showLoader();

      try {
        if (isEdit && eventData?.walletId) {
            values.balance = eventData.balance + values.amount;
            values.amount = 0;
          await updateWallet(eventData.walletId, { id: eventData.walletId, ...values });
          toast.success("Cartera actualizada correctamente", { autoClose: 2000 });
        } else {
            values.balance = values.amount;
            values.amount = 0;
          await addWallet(values);
          toast.success("Cartera agregada correctamente", { autoClose: 2000 });
        }
        handleCloseModal();
        fetchWallets();
      } catch (error) {
        toast.error("Error saving wallet: " + error, { autoClose: 2000 });
      } finally {
        hideLoader();
      }
    }
  });

  useEffect(() => {
    if (isEdit && eventData) {
      formik.setValues({
        name: eventData.name || '',
        description: eventData.description || '',
        amount: eventData.amount || 0,
        balance: eventData.balance || 0,
      });
    } else if (!isEdit && !eventData) {
      formik.resetForm();
    }
  }, [eventData, isEdit]);

  const handleCloseModal = () => {
    setShow(false);
    setEventData(null);
    setIsEdit(false);
    formik.resetForm();
  };

  const handleCreateClick = () => {
    setEventData(null);
    setIsEdit(false);
    formik.resetForm();
    setShow(true);
  };

  const onClickDelete = (cell: WalletDTO) => {
    setEventData(cell);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (eventData?.walletId) {
      showLoader();

      try {
        await deleteWallet(eventData.walletId);
        toast.success("Cartera eliminada correctamente", { autoClose: 2000 });
        setDeleteModal(false);
        fetchWallets();
      } catch (err) {
        toast.error("Error deleting wallet: " + err, { autoClose: 2000 });
      }
    }
  };

  const handleUpdateDataClick = (ele: WalletDTO) => {
    setEventData(ele);
    setIsEdit(true);
    setShow(true);
  };

  const columns = useMemo(() => [
    {
        header: "Cartera",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
    },
    {
      header: "Descripción",
      accessorKey: "description",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Saldo",
      accessorKey: "balance",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Acciones",
      cell: ({ row }: any) => (
        <div className="flex justify-center">
          <Dropdown className="relative">
            <Dropdown.Trigger className="flex items-center justify-center size-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-600 hover:text-white focus:outline-none">
              <MoreHorizontal className="size-4" />
            </Dropdown.Trigger>
            <Dropdown.Content className="absolute z-50 right-0 mt-2 w-40 bg-white rounded-md shadow-lg dark:bg-zink-600">
              <ul>
                <li>
                  <button
                    type="button"
                    onClick={() => handleUpdateDataClick(row.original)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-500"
                  >
                    <FileEdit className="inline size-4 mr-2" />
                    Editar
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => onClickDelete(row.original)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-500"
                  >
                    <Trash2 className="inline size-4 mr-2" />
                    Eliminar
                  </button>
                </li>
              </ul>
            </Dropdown.Content>
          </Dropdown>
        </div>
      ),
    }
  ], []);

  return (
    <div className='card'>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
      />

      <ToastContainer closeButton={false} limit={1} />

      <div className="card-body">
        <div className="flex justify-end mb-4">
          <button onClick={handleCreateClick} className="btn bg-custom-500 text-white">
            <Plus className="inline size-4 mr-1" /> Agregar Wallet
          </button>
        </div>
      </div>

      <div className='card-body'>
        <TableContainer
          isPagination={true}
          isSelect={true}
          isGlobalFilter={true}
          columns={columns}
          data={data}
          customPageSize={5}
          divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
          tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
          theadclassName="border-b border-slate-200 dark:border-zink-500"
          trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
          thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
          tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
          PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
        />
      </div>

      <Modal show={show} onHide={handleCloseModal} modal-center
        className="fixed inset-0 z-drawer flex items-center justify-center"
        dialogClassName="w-screen max-w-md bg-white shadow-xl rounded-xl dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between px-6 py-4 border-b dark:border-zink-500">
          <Modal.Title className="text-lg font-semibold text-slate-900 dark:text-zink-100">
            {isEdit ? "Editar Billetera" : "Agregar Billetera"}
          </Modal.Title>
          <button onClick={handleCloseModal} className="text-slate-400 hover:text-red-500 transition">
            ×
          </button>
        </Modal.Header>

        <Modal.Body className="p-6">
          <form onSubmit={formik.handleSubmit} className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label
              htmlFor="nameInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Nombre de la Cartera <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese nombre de la cartera"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name ? (
              <div className="text-red-500">{formik.errors.name}</div>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="balanceInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Saldo Actual <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="balance"
              readOnly
              className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              value={formik.values.balance}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>

          <div>
            <label
              htmlFor="amountInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Agregar Fondos <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount"
              className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese monto"
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.amount && formik.errors.amount ? (
              <div className="text-red-500">{formik.errors.amount}</div>
            ) : null}
          </div>

          <div className="md:col-span-2">
              <label
                htmlFor="description"
                className="inline-block mb-2 text-base font-medium"
              >
                Descripción
              </label>
              <textarea
                name="description"
                className="form-textarea w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500 focus:border-custom-500 dark:focus:ring-custom-800 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
        </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleCloseModal} className="btn bg-white text-red-500 border border-red-500 hover:bg-red-50 transition">
                Cancelar
              </button>
              <button type="submit" className="btn bg-custom-500 text-white hover:bg-custom-600 transition">
                {isEdit ? "Actualizar" : "Agregar"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WalletList;